import { useState, useEffect, useCallback } from "react";
import { addressFormatter } from "../Components/Formatting/Address";
import API from "./API";
import { useMobile } from "../Components/Mobile";
import { googleKey } from './Keys';
import { usePreference } from "../Data/Preference";
import { useHelp } from "../Components/Help";
import { useOffline } from "../Components/Offline";
import { useBusinessCoordinates } from "../Data/Business";
import { useSearchParams } from 'react-router-dom';

const isLocal = () => {
    return parseInt(window.location.port) > 1000;
}

const deviceIsMd = () => {
    return window.innerWidth >= 768
}

const hashCode = (...args) => {
    const str = JSON.stringify(args);

    let hash = 0;
    for (let i = 0, len = str.length; i < len; i++) {
        let chr = str.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0; // Convert to 32bit integer
    }

    return Math.abs(hash).toString(36);
}

const has = (value, hasVal = true, hasntVal = false) => {
    if (!value) {
        return hasntVal;
    }

    if (typeof value === 'string') {
        return value.trim().length ? hasVal : hasntVal;
    }

    if (Array.isArray(value)) {
        return value.filter(v => !!v).length ? hasVal : hasntVal;
    }

    return Boolean(value ? hasVal : hasntVal);
}

const classNames = (...classes) => {
    return classes.filter(Boolean).join(' ')
}

const toQueryString = (obj) => {
    if (!obj) {
        return '';
    }

    return Object.keys(obj).reduce((search, key) => {
        const val = obj[key];

        if (val === undefined || val === null) {
            return search;
        }

        if (Array.isArray(val)) {
            val.forEach((v) => {
                search.append(`${key}[]`, v);
            });
        } else {
            search.append(key, val);
        }

        return search;
    }, new URLSearchParams()).toString();
}

const getEntityLink = (model, id, params) => {

    const map ={
        'Quotes/Quote':                 '/manage/quotes/' + id,
        'Payments/ConnectedBank':       '/settings/payments/connected-banks',
        'Payments/Account':             '/payments',
        'Payments/Payment':             '/manage/payments/' + id,
        'Balances/Balance':             '/balance/',
        'Marketplace/Subscription':     '/marketplace/subscription',
        'Marketplace/Lead':             '/plan/leads/' + id,
        'Customers/Customer':           '/manage/customers/' + id,
        'Rounds/Round':                 '/plan/rounds/' + id,
        'Services/Service':             '/business/services/' + id,
        'Templates/Template':           '/settings/message-templates/' + id,
        'Soapy/WhatsNewNotification':   '/whats-new',
        'Documents/Document':           '/business/documents/' + id,
        'Broadcasts/Broadcast':           '/more/broadcasts/' + id,
        'Users/AuthDevice':             '/user/security',
        'Users/LoginSession':           '/user/security',
    }

    let query = '';
    if (params) {
        query = '?'+toQueryString(params);
    }

    return (map[model] ?? model)+query;
}

const useVisibilityChange = (handleVisibilityChange, onlyVisible = true) => {
    const onVisibilityChange = () => {
        let eventName;
        if (typeof document.hidden !== 'undefined') {
            eventName = 'visibilitychange';
        } else if (typeof document.msHidden !== 'undefined') {
            eventName = 'msvisibilitychange';
        } else if (typeof document.webkitHidden !== 'undefined') {
            eventName = 'webkitvisibilitychange'
        }

        const theFunction = () => {
            if (onlyVisible && isDocumentHidden()) {
                return
            }
            handleVisibilityChange(isDocumentHidden())
        };

        document.addEventListener(eventName, theFunction, false);

        return () => {
            document.removeEventListener(eventName, theFunction, false)
        };
    }

    useEffect(() => {
        return onVisibilityChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleVisibilityChange]);
}

const isDocumentHidden = () => {
    if (typeof document.hidden !== 'undefined') {
        return document['hidden'];
    } else if (typeof document.msHidden !== 'undefined') {
        return document['msHidden'];
    } else if (typeof document.webkitHidden !== 'undefined') {
        return document['webkitHidden'];
    }
}

const weekString = (_dt = null) => {
    const dt = _dt ? new Date(_dt.getFullYear(), _dt.getMonth(), _dt.getDate()) : new Date();

    const utcDate = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));

    // ISO-8601 week date:
    // - Weeks start on Monday
    // - Week 1 is the week with 4 January in it
    const dayOfWeek = utcDate.getUTCDay() || 7; // 1..7 (Mon..Sun)
    utcDate.setUTCDate(utcDate.getUTCDate() + 4 - dayOfWeek); // move to Thursday

    const isoYear = utcDate.getUTCFullYear();
    const jan4 = new Date(Date.UTC(isoYear, 0, 4));
    const jan4Day = jan4.getUTCDay() || 7;
    const week1Monday = new Date(jan4);
    week1Monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1);

    const weekNum = Math.floor((utcDate - week1Monday) / 86400000 / 7) + 1;

    return `${isoYear}-W${numPad(weekNum)}`;
}

const weeksInYear = (year) => {
    const yearsWith53Weeks = [
        2000,
        2004,
        2008,
        2012,
        2016,
        2020,
        // 2023,
        2026,
        2032,
        2037,
        2043,
        2048,
    ];

    return yearsWith53Weeks.indexOf(parseInt(year)) > -1 ? 53 : 52;
}

const numPad = (str) => {
    return String(str).length === 1 ? '0' + str : str;
}

const dateToString = (date) => {
    return `${date.getFullYear()}-${numPad(date.getMonth() + 1)}-${numPad(date.getDate())}`;
}

const timeToString = (date, seconds = false) => {
    const _seconds = seconds ? `:${numPad(date.getSeconds())}` : '00';
    return `${numPad(date.getHours())}:${numPad(date.getMinutes())}:${_seconds}`;
}

const dateTimeToString = (date, divider = ' ') => {
    return `${dateToString(date)}${divider}${timeToString(date)}`;
}

const useNewWindow = () => {
    // {request, isMobile}
    const mobile = useMobile();

    return (url) => {
        if (mobile && mobile.isMobile) {
            mobile.request('openUrl', { url });
        } else {
            window.open(url);
        }
    }
}

const useDownloadItemisation = () => {
    const newWindow = useNewWindow();

    return (businessId, itemisationId) => newWindow(
        `${API.domain.replace('app.', '').replace('/api/', '')}/itemisations/${businessId}/${itemisationId}.pdf`
    )
}

const useDownloadDocument = () => {
    const newWindow = useNewWindow();

    return (documentId, key) => newWindow(
        `${API.domain.replace('app.', '').replace('/api/', '')}/documents/${documentId}/${key}.pdf`
    )
}

const useDirectionsTo = () => {
    const newWindow = useNewWindow();
    const [directionsApp] = usePreference('work:directionsApp', 'googleMaps');

    return (address) => {
        let addressEncoded;
        if (address.customCoordinates && address.coordinates?.length === 2) {
            addressEncoded = `${address.coordinates[0]},${address.coordinates[1]}`;
        } else {
            addressEncoded = encodeURIComponent(addressFormatter(address));
        }

        const urls = {
            appleMaps: `http://maps.apple.com/?daddr=${addressEncoded}`,
            googleMaps: `https://www.google.com/maps/dir/?api=1&map_action=map&travelmode=driving&dir_action=navigate&destination=${addressEncoded}`,
            waze: `https://waze.com/ul?q=${addressEncoded}&navigate=yes`,
        };

        newWindow(urls[directionsApp] ?? urls.googleMaps);
    }
}

const getGoogleMapsRouteLink = (businessCoordinates, jobs) => {
    const businessCoords = `${businessCoordinates.latitude},${businessCoordinates.longitude}`;
    const waypoints = jobs.map(j => j.coordinates.join(',')).join('|');

    const baseUrl = 'https://www.google.com/maps/dir/?api=1';
    const params = [
        `origin=${encodeURIComponent(businessCoords)}`,
        `destination=${encodeURIComponent(businessCoords)}`,
        'travelmode=driving'
    ];

    if (waypoints) {
        params.push(`waypoints=${encodeURIComponent(waypoints)}`);
    }

    return `${baseUrl}&${params.join('&')}`;
};
/*
const getAppleMapsRouteLink = (businessCoordinates, jobs) => {
    const businessCoords = `${businessCoordinates.latitude},${businessCoordinates.longitude}`;

    // Apple Maps supports multiple daddr parameters for multi-stop trips (undocumented feature)
    // Route: Business -> Job1 -> Job2 -> ... -> JobN -> Business
    const baseUrl = 'http://maps.apple.com/?dirflg=d';
    const allStops = [businessCoords, ...jobs.map(j => j.coordinates.join(',')), businessCoords];
    const daddrParams = allStops.map(stop => `daddr=${encodeURIComponent(stop)}`).join('&');

    return `${baseUrl}&${daddrParams}`;
}

const getWazeRouteLink = (businessCoordinates, jobs) => {
    const businessCoords = `${businessCoordinates.longitude},${businessCoordinates.latitude}`;

    const allCoords = [businessCoords, ...jobs.map(j => `${j.coordinates[1]},${j.coordinates[0]}`), businessCoords];
    const routeString = allCoords.join(' to ');

    // Use Waze with a route string that includes all stops
    return `https://waze.com/ul?q=${encodeURIComponent(routeString)}&navigate=yes`;
}
 */
const useRouteLink = () => {
    const newWindow = useNewWindow();
    // const [directionsApp] = usePreference('work:directionsApp', 'googleMaps');
    const { coordinates: businessCoordinates } = useBusinessCoordinates();

    return (jobs) => {
        if (!jobs || jobs.length < 1) {
            return null;
        }

        const jobsWithCoordinates = jobs
            .map(j => ({ ...j, coordinates: j.customer.address.coordinates }))
            .filter(j => j.coordinates);

        if (jobsWithCoordinates.length < 1) {
            return null;
        }

        // const urls = {
        //     appleMaps: () => getAppleMapsRouteLink(businessCoordinates, jobsWithCoordinates),
        //     googleMaps: () => getGoogleMapsRouteLink(businessCoordinates, jobsWithCoordinates),
        //     waze: () => getWazeRouteLink(businessCoordinates, jobsWithCoordinates),
        // };

        // const url = match(directionsApp, urls)();
        const url = getGoogleMapsRouteLink(businessCoordinates, jobsWithCoordinates);

        newWindow(url);
    };
};

const ucFirst = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
}


const numFormat = (num, hideDecimals) => {
    if (num === null || num === undefined) {
        return 0;
    }
    const str = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    if (hideDecimals) {
        return str.split('.')[0];
    } else if (hideDecimals === false) {
        const parts = str.split('.');
        if (parts[1] === undefined) {
            parts[1] = '00';
        } else if (parts[1] && parts[1].length < 2) {
            parts[1] += '0'.repeat(2 - parts[1].length);
        }
        return parts.join('.');
    }
    return str;
}

const plural = (count, zero, singular, plural) => {
    const int = parseInt(count ?? 0);
    if (int === 0 || isNaN(int)) {
        return zero;
    }

    if (int === 1) {
        return singular;
    }

    return plural;
}

const strToHsl = (str) => {
    if (!str) {
        return 'hsl(0, 50%, 50%)';
    }

    const h = str.split('').reduce((total, s) => {
        const x = total += s.charCodeAt(0);
        return x >= 360 ? x - 360 : x;
    }, 0);

    return `hsl(${h}, 100%, 45%)`;
}

const hexToRgba = (hex, opacity = 1) => {
    const h = hex.replace('#', '').replace(' ', '');

    const r = parseInt(h.substr(0, 2), 16);
    const g = parseInt(h.substr(2, 2), 16);
    const b = parseInt(h.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

const getContrastColor = (color, name) => {
    const _getVal = (_yiq) => {
        if (_yiq >= 128) {
            return name ? 'black' : '#000';
        } else {
            return name ? 'white' : '#FFF';
        }
    }

    // HSL
    if (color.indexOf(',') !== -1) {
        const [h, s, l] = color.split(',').map(v => v.replace('%', '').replace(' ', ''));
        const yiq = ((h * 299) + (s * 587) + (l * 114)) / 1000;
        return _getVal(yiq);
    }

    // Hex
    const hex = color.replace('#', '');
    var r = parseInt(hex.substr(0, 2), 16);
    var g = parseInt(hex.substr(2, 2), 16);
    var b = parseInt(hex.substr(4, 2), 16);
    var yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;

    return _getVal(yiq);
}

const b64SafeEncode = (str) => {
    return btoa(str).replace('+', '-').replace('/', '_').replace(/=+$/, '')
}

const b64SafeDecode = (str) => {
    return atob(str.replace('-', '+').replace('_', '/'));
}

const preloaded = [];
const googleMapsAPI = `https://maps.googleapis.com/maps/api/js?libraries=maps&key=${googleKey}&v=weekly&map_ids=&language=en&region=gb&auth_referrer_policy=origin&callback=google.maps.__ib__`;
const preload = (path) => {
    if (preloaded.includes(path)) {
        return;
    }

    preloaded.push(path);

    const link       = document.createElement('link');
    link.as          = path.indexOf('.css') !== -1 ? 'style' : 'script';
    link.rel         = 'prefetch';
    link.href        = path;
    document.head.appendChild(link);
}

const stylesheets = [];
const stylesheetAliases = {
    fonts: 'https://getsoapy.com/css/fonts.css',
}
const loadStylesheet = (href) => {
    if (stylesheetAliases[href]) {
        href = stylesheetAliases[href];
    }

    if (stylesheets.includes(href)) {
        return;
    }

    stylesheets.push(href);

    const link       = document.createElement('link');
    link.rel          ='stylesheet';
    link.href        = href;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
}

const useOpenVideo = () => {
    const newWindow = useNewWindow();

    return (videoId) => {
        newWindow(videoId.replace(/^yt:/, 'https://www.youtube.com/watch?v='));
    }
}

const useRedirect = () => {
    // const [redirect, setRedirect] = useState(null);
    const {redirect} = useHelp();

    // useEffect(() => {
    //     if (!redirect) {
    //         return;
    //     }

    //     if (redirect.trim() === window.location.pathname.trim()) {
    //         setRedirect(null);
    //     }
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [window.location.pathname, redirect])

    return {
        go: redirect,
        Redirect: () => null,
    }
}

const useSearchParamsToObject = (searchParams, setter) => {
    useEffect(() => {
        if (!searchParams || !searchParams.entries()) {
            return;
        }
        const out = {};
        const entries = searchParams.entries();
        for (const [key, value] of entries) {
            if (key.substr(-2) === '[]') {
                const _key = key.substr(0, key.length - 2);
                if (!out[_key]) {
                    out[_key] = [];
                }
                out[_key].push(value);
                continue;
            }

            out[key] = value;
        }
        // Don't set if empty, as it will reset any persisted searches
        if (Object.keys(out).length === 0) {
            return;
        }

        setter(out);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);
}

const useSearchParamsOnce = () => {
    const [sps, set] = useSearchParams();
    const [searchData, setSearchData] = useState({});
    useSearchParamsToObject(sps, (data) => {
        setSearchData(data);
        set(new URLSearchParams());
    });

    return searchData;
}

const parseDuration = (duration) => {
    // We're dealing with seconds
    if (typeof duration === 'number') {
        const hours = Math.floor(duration / 3600);
        const minutes = Math.floor((duration % 3600) / 60);
        const seconds = duration % 60;
        return {hours, minutes, seconds, valid: true};
    }

    if (!duration || typeof duration !== 'string') {
        return {hours: 0, minutes: 0, seconds: 0, valid: false};
    }

    const parts = duration.split(':');
    const hours = parseInt(parts[0] ?? 0);
    const minutes = parseInt(parts[1] ?? 0);
    const seconds = parseInt(parts[2] ?? 0);

    return {hours, minutes, seconds, valid: true};
}

const durationToSeconds = (duration) => {
    const {hours, minutes, seconds} = parseDuration(duration);

    return (hours * 3600) + (minutes * 60) + seconds;
}

const secondsToDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const _seconds = Math.floor(seconds % 60);

    return `${numPad(hours)}:${numPad(minutes)}:${numPad(_seconds)}`;
}

const timeToPx = (time, pxPerHour) => {
    if (!time) {
        return null;
    }

    const [hour, minute] = time.split(':');

    return Math.round(
        (parseInt(hour) * pxPerHour) + (parseInt(minute) * pxPerHour / 60)
    );
}

const sleep = (seconds) => {
    return new Promise(resolve => setTimeout(resolve, seconds >= 900 ? seconds : seconds * 1000));
}

const useCurrentLocation = () => {
    const [location, setLocation] = useState(null);

    useEffect(() => {
        if (!navigator.geolocation) {
            return;
        }

        navigator.geolocation.getCurrentPosition((position) => {
            setLocation({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
            });
        });
    }, []);

    return location;
}

const marshalCoords = (data) => {
    if (!data) {
        return null;
    }

    if (Array.isArray(data)) {
        return {
            latitude: data[0],
            longitude: data[1],
        }
    }
    if (data?.lat) {
        return {
            latitude: data.lat,
            longitude: data.lng,
        }
    }
    if (data?.latitude) {
        return {
            latitude: data.latitude,
            longitude: data.longitude,
        }
    }

    return null;
}

const flipFlops = {};
const useFlipFlop = (onTime, offTime) => {
    const [state, setState] = useState(false);
    const name = `${onTime}-${offTime}`;

    useEffect(() => {
        if (state) {
            flipFlops[name] = setTimeout(() => setState(false), onTime);
        } else {
            flipFlops[name] = setTimeout(() => setState(true), offTime);
        }

        return () => clearTimeout(flipFlops[name]);
    }, [name, onTime, offTime, state]);

    return state;
}

const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
}

const match = (key, obj, fallback = null) => {
    return obj[key] ?? obj['default'] ?? fallback
}

const useBackOnline = (callback) => {
    const {backOnline} = useOffline();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => backOnline.listen(callback), []);
}

const val = (x) => {
    return typeof x === 'function' ? x() : x;
}

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
}

const useDebounceCallback = (callback, delay) => {
    const trigger = useCallback(() => {
        const handler = setTimeout(() => callback(), delay);
        return () => clearTimeout(handler);
    }, [callback, delay]);

    return trigger;
}

const timestamp = () => {
    return Math.round(new Date().getTime() / 1000);
}

const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

const useKeyboardDetection = () => {
    const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

    useEffect(() => {
        // iOS Safari keyboard events
        document.addEventListener('focusin', (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.contentEditable === 'true') {
                setIsKeyboardOpen(true);
            }
        });

        document.addEventListener('focusout', (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.contentEditable === 'true') {
                // Small delay to allow for keyboard animation
                setTimeout(() => setIsKeyboardOpen(false), 100);
            }
        });

        // Android Chrome keyboard events
        if ('virtualKeyboard' in navigator) {
            navigator.virtualKeyboard.addEventListener('geometrychange', (e) => {
                setIsKeyboardOpen(e.target.boundingRect.height > 0);
            });
        }

        // Fallback: visualViewport API for broader compatibility
        if (window.visualViewport) {
            let initialViewportHeight = window.visualViewport.height;

            const handleViewportChange = () => {
                const currentHeight = window.visualViewport.height;
                const heightDifference = initialViewportHeight - currentHeight;
                const keyboardThreshold = 150;
                const newKeyboardState = heightDifference > keyboardThreshold;

                if (newKeyboardState !== isKeyboardOpen) {
                    setIsKeyboardOpen(newKeyboardState);
                }
            };

            window.visualViewport.addEventListener('resize', handleViewportChange);

            return () => {
                window.visualViewport.removeEventListener('resize', handleViewportChange);
            };
        }

        return () => {
            // Cleanup is handled by the browser for focus events
        };
    }, [isKeyboardOpen]);

    return isKeyboardOpen;
};

const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return '0s';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
        return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
        return `${minutes}m ${secs}s`;
    } else {
        return `${secs}s`;
    }
};

const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';

    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');

    // UK number formatting
    if (cleaned.startsWith('44') && cleaned.length === 12) {
        return `+44 ${cleaned.slice(2, 6)} ${cleaned.slice(6, 10)} ${cleaned.slice(10)}`;
    }

    // US number formatting
    if (cleaned.startsWith('1') && cleaned.length === 11) {
        return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }

    // Default formatting for other numbers
    if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }

    // Return original if no pattern matches
    return phoneNumber;
};

const ltrim = (str, chars) => {
    return str.replace(new RegExp(`^[${chars}]+`), '');
}

export {
    deviceIsMd,
    classNames,
    dateTimeToString,
    dateToString,
    getEntityLink,
    has,
    hashCode,
    isDocumentHidden,
    useDownloadItemisation,
    useDownloadDocument,
    useNewWindow,
    weekString,
    weeksInYear,
    numPad,
    timeToString,
    toQueryString,
    useVisibilityChange,
    plural,
    ucFirst,
    numFormat,
    useDirectionsTo,
    getGoogleMapsRouteLink,
    useRouteLink,
    strToHsl,
    hexToRgba,
    getContrastColor,
    b64SafeEncode,
    b64SafeDecode,
    googleMapsAPI,
    preload,
    loadStylesheet,
    isLocal,
    useOpenVideo,
    useRedirect,
    useSearchParamsToObject,
    useSearchParamsOnce,
    parseDuration,
    durationToSeconds,
    secondsToDuration,
    timeToPx,
    sleep,
    useCurrentLocation,
    marshalCoords,
    useFlipFlop,
    getCookie,
    match,
    useBackOnline,
    val,
    useDebounce,
    useDebounceCallback,
    timestamp,
    generateUUID,
    useKeyboardDetection,
    formatDuration,
    formatPhoneNumber,
    ltrim,
};
